import { useQueryClient } from '@tanstack/react-query'
import { Check, ChevronDown, EllipsisVertical, Loader2, Pause, Play, RotateCcw, X } from 'lucide-react'
import { useContext, useEffect, useState } from 'react'

import BrokersMemberContext, { type BrokersMemberContextType } from 'components/context/BrokersMemberContext'
import { type PaginatedResponse } from 'components/hooks/types'
import { Badge, Button, DropdownMenu, IconButton } from 'slab'
import { capitalize } from 'utils'
import {
  getGetBrokersTaskWorkflowsIdQueryKey,
  getGetBrokersTaskWorkflowsQueryKey,
  usePatchBrokersTasks,
  usePostBrokersTaskWorkflowsIdAction,
} from 'utils/web_api'
import {
  type BrokersTask,
  type BrokersWorkflow,
  type BrokersWorkflowAvailableActionsItem,
} from 'utils/web_api.schemas'

import { sortAvailableActions } from './WorkflowVisualIndicators'

// Task Settings Popover Component
export const TaskSettingsPopover = ({
  task,
  workflowId,
  withPortal,
}: {
  task: BrokersTask
  workflowId: string
  withPortal?: boolean
}) => {
  const queryClient = useQueryClient()
  const taskMutation = usePatchBrokersTasks()

  const handleUpdateExecutionMethod = (method: 'automated' | 'manual') => {
    const message = `Task execution method changed from ${task.execution_method} to ${method} by broker`

    taskMutation.mutate(
      {
        data: {
          task_ids: [task.id],
          method,
          method_message: message,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getGetBrokersTaskWorkflowsIdQueryKey(workflowId),
          })
        },
        onError: (error) => {
          console.error('Failed to update execution method:', error)
          alert('Failed to update execution method. Please try again.')
        },
      }
    )
  }

  const handleUpdateExecutionContext = (context: 'thatch' | 'member') => {
    const message = `Task execution context changed from ${task.execution_context} to ${context} by broker`

    taskMutation.mutate(
      {
        data: {
          task_ids: [task.id],
          execution_context: context,
          context_message: message,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getGetBrokersTaskWorkflowsIdQueryKey(workflowId),
          })
        },
        onError: (error) => {
          console.error('Failed to update execution context:', error)
          alert('Failed to update execution context. Please try again.')
        },
      }
    )
  }

  return (
    <DropdownMenu
      align='end'
      withPortal={withPortal}
      trigger={
        <IconButton
          label='Task settings'
          variant='secondary'
          icon={EllipsisVertical}
          disabled={taskMutation.isPending}
        />
      }
    >
      {/* Execution Method Section */}
      <DropdownMenu.Section title='Method'>
        <DropdownMenu.Item
          onSelect={() => handleUpdateExecutionMethod('automated')}
          disabled={
            task.execution_method === 'automated' ||
            taskMutation.isPending ||
            !task.allowed_method_changes?.includes('automated')
          }
        >
          <div className='flex items-center justify-between w-full space-x-1'>
            <span>Automated</span>
            {task.execution_method === 'automated' && (
              <Badge type='info'>Current</Badge>
            )}
          </div>
        </DropdownMenu.Item>
        <DropdownMenu.Item
          onSelect={() => handleUpdateExecutionMethod('manual')}
          disabled={
            task.execution_method === 'manual' ||
            taskMutation.isPending ||
            !task.allowed_method_changes?.includes('manual')
          }
        >
          <div className='flex items-center justify-between w-full space-x-1'>
            <span>Manual</span>
            {task.execution_method === 'manual' && (
              <Badge type='info'>Current</Badge>
            )}
          </div>
        </DropdownMenu.Item>
      </DropdownMenu.Section>

      {/* Execution Context Section */}
      <DropdownMenu.Section title='Context'>
        <DropdownMenu.Item
          onSelect={() => handleUpdateExecutionContext('thatch')}
          disabled={task.execution_context === 'thatch' || taskMutation.isPending}
        >
          <div className='flex items-center justify-between w-full space-x-1'>
            <span>Thatch</span>
            {task.execution_context === 'thatch' && (
              <Badge type='info'>Current</Badge>
            )}
          </div>
        </DropdownMenu.Item>
        <DropdownMenu.Item
          onSelect={() => handleUpdateExecutionContext('member')}
          disabled={task.execution_context === 'member' || taskMutation.isPending}
        >
          <div className='flex items-center justify-between w-full space-x-1'>
            <span>Member</span>
            {task.execution_context === 'member' && (
              <Badge type='info'>Current</Badge>
            )}
          </div>
        </DropdownMenu.Item>
      </DropdownMenu.Section>
    </DropdownMenu>
  )
}

// Task Action Controls Component
export const TaskActionControls = ({
  task,
  onTaskAction,
  isLoading,
  workflowId,
  withPortal,
}: {
  task: BrokersTask
  onTaskAction: (taskId: string, action: string) => void
  isLoading: boolean
  workflowId: string
  withPortal?: boolean
}) => {
  const { isUserThatchWorkflowAdmin } = useContext<BrokersMemberContextType>(BrokersMemberContext)

  const [selectedAction, setSelectedAction] = useState<string>('')

  // Auto-select first available action
  useEffect(() => {
    if (
      task.available_actions &&
      task.available_actions.length > 0 &&
      !selectedAction
    ) {
      setSelectedAction(task.available_actions[0])
    }
  }, [task.available_actions, selectedAction])

  if (!task.available_actions || task.available_actions.length === 0) {
    return null
  }

  const isRollbackableCompletedTask = task.status === 'completed' && task.available_actions.includes('rollback')

  const isExecutableManualTask =
    task.can_execute &&
    task.execution_method === 'manual' &&
    task.status === 'pending'
  const isRetryableFailedTask = task.status === 'failed'

  // Only show actions for manual pending tasks or retryable failed tasks
  if (!isRollbackableCompletedTask && !isExecutableManualTask && !isRetryableFailedTask && !isUserThatchWorkflowAdmin) {
    return null
  }

  const handleExecuteAction = () => {
    if (selectedAction) {
      onTaskAction(task.id, selectedAction)
    }
  }

  const getActionIcon = () => {
    if (isLoading) return <Loader2 className='w-3 h-3 animate-spin' />

    switch (selectedAction) {
      case 'complete':
        return <Check className='w-4 h-4' />
      case 'cancel':
        return <X className='w-4 h-4' />
      case 'uncancel':
        return <Play className='w-4 h-4' />
      case 'retry':
        return <RotateCcw className='w-4 h-4' />
      case 'rollback':
        return <RotateCcw className='w-4 h-4' />
      default:
        return <Check className='w-4 h-4' />
    }
  }

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'complete':
        return 'Complete'
      case 'cancel':
        return 'Cancel'
      case 'uncancel':
        return 'Uncancel'
      case 'rollback':
        return 'Rollback'
      default:
        return capitalize(action)
    }
  }

  return (
    <div className='flex items-center space-x-1'>
      <DropdownMenu
        withPortal={withPortal}
        trigger={
          <Button variant='secondary' size='small' rightIcon={ChevronDown}>
            {getActionLabel(selectedAction)}
          </Button>
          }
      >
        {task.available_actions.map((action) => (
          <DropdownMenu.Item
            key={action}
            onSelect={() => setSelectedAction(action)}
          >
            {getActionLabel(action)}
          </DropdownMenu.Item>
        ))}
      </DropdownMenu>

      <Button
        size='small'
        variant='primary'
        onClick={handleExecuteAction}
        disabled={!selectedAction || isLoading}
      >
        {getActionIcon()}
      </Button>

      <TaskSettingsPopover task={task} workflowId={workflowId} withPortal={withPortal} />
    </div>
  )
}

// Workflow Action Controls Component
export const WorkflowActionControls = ({
  workflow,
  onSuccess,
}: {
  workflow: BrokersWorkflow
  onSuccess?: (updatedWorkflow: BrokersWorkflow) => void
}) => {
  const [selectedAction, setSelectedAction] = useState<
    BrokersWorkflowAvailableActionsItem | undefined
  >()
  const queryClient = useQueryClient()

  // Auto-select first available action and sync when available actions change
  useEffect(() => {
    const sortedActions = sortAvailableActions(workflow.available_actions)
    if (sortedActions && sortedActions.length > 0) {
      // If no action is selected, or the current action is no longer available, select the first available action
      if (!selectedAction || !sortedActions.includes(selectedAction)) {
        setSelectedAction(sortedActions[0])
      }
    } else {
      // No actions available, clear selection
      setSelectedAction(undefined)
    }
  }, [workflow.available_actions, selectedAction])

  const mutation = usePostBrokersTaskWorkflowsIdAction()

  const handleExecuteAction = () => {
    if (!selectedAction) return

    const requestData: any = {
      id: workflow.id,
      action: selectedAction,
    }

    // Add cancellation_reason for cancel action
    if (selectedAction === 'cancel') {
      const reason = prompt('Enter cancellation reason (optional):')
      if (reason) {
        requestData.data = { cancellation_reason: reason }
      }
    }

    mutation.mutate(requestData, {
      onSuccess: (updatedWorkflow) => {
        // Update the individual workflow cache directly with returned data
        queryClient.setQueryData(
          getGetBrokersTaskWorkflowsIdQueryKey(workflow.id),
          updatedWorkflow
        )

        // Update the workflow in the list cache by finding and replacing it
        queryClient.setQueryData(
          getGetBrokersTaskWorkflowsQueryKey({
            'filters[enrollment_ids][]': [workflow.insurance_enrollment_id],
            'sort[status]': 'asc',
          }),
          (oldData: PaginatedResponse<BrokersWorkflow>) => {
            if (!oldData?.data) return oldData

            return {
              ...oldData,
              data: oldData.data.map((w: BrokersWorkflow) =>
                w.id === workflow.id ? updatedWorkflow : w
              ),
            }
          }
        )

        // Call the optional onSuccess callback
        onSuccess?.(updatedWorkflow)
      },
      onError: (error) => {
        console.error('Failed to execute workflow action:', error)
        alert('Failed to execute action. Please try again.')
      },
    })
  }

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'start':
        return 'Start'
      case 'complete':
        return 'Complete'
      case 'cancel':
        return 'Cancel'
      case 'pause':
        return 'Pause'
      case 'resume':
        return 'Resume'
      case 'reopen':
        return 'Reopen'
      default:
        return capitalize(action)
    }
  }

  const WorkflowActionButton = ({
    selectedAction,
    isPending,
    handleExecuteAction,
  }: {
    selectedAction: BrokersWorkflowAvailableActionsItem | undefined
    isPending: boolean
    handleExecuteAction: () => void
  }) => {
    const getActionIcon = () => {
      if (isPending) return <Loader2 className='w-4 h-4 animate-spin' />

      switch (selectedAction) {
        case 'start':
        case 'resume':
          return <Play className='w-4 h-4' />
        case 'pause':
          return <Pause className='w-4 h-4' />
        case 'cancel':
          return <X className='w-4 h-4' />
        case 'complete':
          return <Check className='w-4 h-4' />
        case 'reopen':
          return <Play className='w-4 h-4' />
        case 'rollback':
          return <RotateCcw className='w-4 h-4' />
        default:
          return <Play className='w-4 h-4' />
      }
    }

    return (
      <Button
        size='small'
        variant='primary'
        onClick={handleExecuteAction}
        disabled={!selectedAction || isPending}
      >
        {getActionIcon()}
      </Button>
    )
  }

  if (!workflow.available_actions || workflow.available_actions.length === 0) {
    return (
      <div className='flex items-center space-x-2'>
        <Button variant='secondary' size='small' disabled>
          No Actions Available
        </Button>
        <WorkflowActionButton
          selectedAction={undefined}
          isPending={false}
          handleExecuteAction={() => {}}
        />
      </div>
    )
  }

  return (
    <div className='flex items-center space-x-2'>
      <DropdownMenu
        trigger={
          <Button variant='secondary' size='small' rightIcon={ChevronDown}>
            {selectedAction ? getActionLabel(selectedAction) : 'Select Action'}
          </Button>
        }
      >
        {workflow.available_actions.map((action) => (
          <DropdownMenu.Item
            key={action}
            onSelect={() => setSelectedAction(action)}
          >
            {getActionLabel(action)}
          </DropdownMenu.Item>
        ))}
      </DropdownMenu>

      <WorkflowActionButton
        selectedAction={selectedAction}
        isPending={mutation.isPending}
        handleExecuteAction={handleExecuteAction}
      />
    </div>
  )
}

// Hook for task actions
type UseTaskActionsOptions = {
  onSuccess?: (taskId: string, action: string) => void
  onError?: (error: unknown) => void
}

export const useTaskActions = (workflowId: string, options?: UseTaskActionsOptions) => {
  const queryClient = useQueryClient()
  const taskMutation = usePatchBrokersTasks()

  const handleTaskAction = (taskId: string, action: string) => {
    const statusMessage = 'Task manually updated by broker'

    taskMutation.mutate(
      {
        data: {
          task_ids: [taskId],
          status_event: action as any,
          status_message: statusMessage,
        },
      },
      {
        onSuccess: () => {
          // Refetch the workflow to get updated task statuses
          queryClient.invalidateQueries({
            queryKey: getGetBrokersTaskWorkflowsIdQueryKey(workflowId),
          })

          options?.onSuccess?.(taskId, action)
        },
        onError: (error) => {
          console.error('Failed to execute task action:', error)
          alert('Failed to execute task action. Please try again.')

          options?.onError?.(error)
        },
      }
    )
  }

  return { handleTaskAction, isLoading: taskMutation.isPending }
}
